import React, {useEffect, useState} from "react";
import {Collapse, Spinner, Tooltip} from "@material-tailwind/react";
import {formatDateString} from "../../utils/daysOptions";
import {useTranslation} from "react-i18next";
import {ProcessDto} from "../../services/interface/ProcessDtoInterface";
import ModalFile from "../../components/general/modal-file";
import {useDeleteDocumentProcess} from "../../services/document/apiDocument";
import {DocumentProcessInterface} from "../../services/interface/DocumentProcessInterface";
import {showToast} from "../../components/general/CustomToast";
import DialogAddDocuments from "./DialogAddDocuments";
import {decodeIdToken} from "../../services/session/apiSession";
import Cookies from "js-cookie";
import {ID_TOKEN} from "../../conf";
import {useSessionStore} from "../../services/storage/session/SessionStorage";
import {RolEnum} from "../../services/enum/PermissionConst";
import {useDocumentService} from "./useDocumentService";
import useBooleanValue from "../../hooks/useBooleanValue";
import CustomIconButton from "../../components/general/button/CustomIconButton";
import {KeyboardArrowDown, Edit, DateRange, NoteAdd, Visibility, Delete} from "@mui/icons-material";

interface DocumentProcessProps {
    item: ProcessDto;
    index: number;
    setProcessDB: React.Dispatch<React.SetStateAction<ProcessDto | undefined>>;
    setOpenCreate: React.Dispatch<React.SetStateAction<boolean>>;
    setTitle: React.Dispatch<React.SetStateAction<string>>;
}

export default function DocumentProcess({item, index, setProcessDB, setOpenCreate, setTitle}: DocumentProcessProps) {
    const {t} = useTranslation("global") as { t: (key: string) => string }
    const user = useSessionStore(data => data.user)

    const {
        mutate: deleteDocument,
        isPending: isLoadingDelete,
        data: successDeleteDocument
    } = useDeleteDocumentProcess();
    const [username, setUsername] = useState("");
    const openDetail = useBooleanValue();
    const [documents, setDocuments] = useState<DocumentProcessInterface[] | undefined>(item?.documents)
    const [document, setDocument] = useState<DocumentProcessInterface | undefined>()
    const [open, setOpen] = React.useState<boolean>(false);
    const [openAddDocs, setOpenAddDocs] = React.useState<boolean>(false);
    const { findDocument, isLoadingDocument, file } = useDocumentService({...{documentItem: document}});

    const toggleOpen = (): void => openDetail.setOpen((cur) => !cur);

    useEffect(() => {
        const user = decodeIdToken(Cookies.get(ID_TOKEN))
        if (user && user?.preferred_username) {
            setUsername(user?.preferred_username.toUpperCase())
        }
    }, []);

    useEffect(() => {
        if (open && document) {
            findDocument({idDocumentRepository: document.idDocumentRepository})
        }
    }, [open, document]);

    useEffect(() => {
        if (successDeleteDocument && documents && documents.length > 0) {
            showToast(t('success-delete-document'), 'success')
            setDocuments(documents.filter((doc) => doc.idDocumentRepository !== successDeleteDocument.idDocumentRepository))
        }
    }, [successDeleteDocument]);

    function PermissionAttach() {
        return !!(item && item.usersAttachDocs && item.usersAttachDocs.filter(permission => permission.idUser === username).length > 0);
    }

    function PermissionDelete() {
        return !!(item && item.usersDeleteDocs && item.usersDeleteDocs.filter(permission => permission.idUser === username).length > 0);
    }

    function PermissionEdit() {
        return !!(item && item.createdBy && item.createdBy.toUpperCase() === username);
    }

    return (
        <div
            key={`process-${index}`}
            className={`${openDetail.open ? 'min-h-[250px]' : ''}bg-white p-2 !border border-secondary rounded-[14px] gap-2`}>
            {open && <ModalFile loading={isLoadingDocument} {...{open, setOpen, file}}/>}
            {openAddDocs && <DialogAddDocuments open={openAddDocs} setOpen={setOpenAddDocs} process={item}/>}
            <div className={'flex flex-row'}>
                <CustomIconButton
                    type={'tooltip'}
                    tooltip={item?.name ?? ''}
                    className={`text-[16px] ${!openDetail.open ? 'truncate' : ''}`}
                    onClick={toggleOpen}
                >
                    {t('process-name')}: {item?.name}
                </CustomIconButton>
                <CustomIconButton
                    className={'ml-auto min-w-[25px]'}
                    tooltip={'view-detail'}
                    onClick={toggleOpen}
                >
                    <KeyboardArrowDown className={`text-[26px] sm:text-[20px] ${openDetail.open ? 'rotate-180' : ''}`}/>
                </CustomIconButton>
            </div>
            <Collapse open={openDetail.open}>
                <>
                    <div className={'flex flex-row'}>
                        {(PermissionEdit() || user?.rol === RolEnum.ADMIN) &&
                            <Tooltip className={'bg-primary text-white'} content={t('edit')}>
                                <Edit onClick={
                                    () => {
                                        setProcessDB(item)
                                        setTitle(t('edit-process'))
                                        setOpenCreate(true)
                                    }
                                }/>
                            </Tooltip>
                        }
                        <Tooltip className={'bg-primary text-white'} content={formatDateString(item?.date)}>
                            <div className={'ml-auto flex items-center truncate'}>
                                <DateRange/>
                                {formatDateString(item?.date)}
                            </div>
                        </Tooltip>
                    </div>
                    {item.description &&
                        <div className={'text-[14px] font-light'}>{item.description}</div>
                    }
                    <div className={'flex flex-col gap-1'}>
                        <div className={'flex flex-row'}>
                            {t('documents')}:
                            {(PermissionAttach() || user?.rol === RolEnum.ADMIN) &&
                                <Tooltip className={'bg-primary text-white'} content={t('add-documents')}>
                                    <NoteAdd className={'ml-auto'} onClick={() => {
                                        setOpenAddDocs(true)
                                    }}/>
                                </Tooltip>
                            }
                        </div>
                        {
                            documents && documents.length > 0 ?
                                <div
                                    className={'grid grid-cols-1 gap-1 max-h-[300px] overflow-y-auto !border border-y-headerMenus border-transparent py-2'}>
                                    {documents.map((doc, indexDoc) => (
                                        <div
                                            key={`process-doc-${item.idProcess}-${indexDoc}`}
                                            className={`bg-blue-gray-50 flex flex-col gap-1 p-2 rounded-[20px] !border border-secondary ${doc.description ? '' : 'min-h-[70px]'}`}
                                        >
                                            <div className={'grid grid-cols-2 items-center'}>
                                                <div className={'flex flex-row items-center'}>
                                                    <DateRange/>
                                                    {formatDateString(doc?.date)}
                                                </div>
                                                <div className={'flex flex-row justify-end gap-3'}>
                                                    <Tooltip className={'bg-primary text-white'} content={t('view')}>
                                                        {
                                                            isLoadingDocument ?
                                                                <Spinner/> :
                                                                <Visibility
                                                                    onClick={() => {
                                                                        if (!isLoadingDelete) {
                                                                            if (doc.type === 'application/pdf') {
                                                                                setDocument(doc);
                                                                                setOpen(true);
                                                                            } else {
                                                                                setDocument(doc);
                                                                                findDocument({idDocumentRepository: doc.idDocumentRepository})
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                        }
                                                    </Tooltip>
                                                    {(PermissionDelete() || user?.rol === RolEnum.ADMIN) &&
                                                        <Tooltip className={'bg-primary text-white'} content={t('delete')}>
                                                            <Delete
                                                                onClick={() => {
                                                                    showToast(
                                                                        t('confirm-delete-document'),
                                                                        'warning-success',
                                                                        () => {
                                                                            deleteDocument({idDocumentRepository: doc.idDocumentRepository})
                                                                        },
                                                                        isLoadingDelete
                                                                    )
                                                                }}
                                                            />
                                                        </Tooltip>}
                                                </div>
                                            </div>
                                            <div className={'text-[12px] font-semibold'}>{doc.name}</div>
                                            {doc?.description &&
                                                <div className={'text-[14px]'}>
                                                    {`${t('description')}: ${doc.description}`}
                                                </div>
                                            }
                                        </div>
                                    ))}
                                </div> :
                                <div
                                    className={'grid !border border-y-headerMenus border-transparent py-2 text-center text-[12px]'}>
                                    {t('not-records')}
                                </div>
                        }
                    </div>
                </>
            </Collapse>
            <div className={'text-[12px] mt-auto'}>{`${t('create-by')}: ${item.createdBy}`}</div>
        </div>
    )
}
